import type { Ref } from 'vue';
import {
  computed,
  ref,
} from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextQuery } from '@modules/core/services/query';
import { useCurrentNamespace } from '@modules/permissions/composables';

import type {
  IPaymentSystemQueryType,
  IPaymentSystemsAllowedResponse,
  IPaymentSystemWithdrawalSchema,
} from '../types';
import {
  ADMIN_PAYMENT_SYSTEM_ALLOWED_URL,
  AFFILIATE_CASHOUT_PAYMENT_SYSTEM_ALLOWED_URL,
  AFFILIATE_PAYMENT_SYSTEM_ALLOWED_URL,
  PAYMENT_SYSTEM_ALLOWED_QUERY_KEY,
} from '../constants';

export default function usePaymentSystemsAllowedQuery(
  affId: Ref<number | undefined>,
  queryType: Ref<IPaymentSystemQueryType> = ref('default'),
): IContextQueryResult<IPaymentSystemsAllowedResponse, Error, IPaymentSystemWithdrawalSchema[]> {
  const { isAdminNamespace } = useCurrentNamespace();
  const { fetchGet } = useRestClient();

  return useContextQuery<IPaymentSystemsAllowedResponse, Error, IPaymentSystemWithdrawalSchema[]>({
    queryKey: [PAYMENT_SYSTEM_ALLOWED_QUERY_KEY, affId, queryType],
    queryFn: async ({ signal }) => {
      let url: string;
      if (isAdminNamespace.value) {
        url = ADMIN_PAYMENT_SYSTEM_ALLOWED_URL;
      } else if (queryType.value === 'cashout') {
        url = AFFILIATE_CASHOUT_PAYMENT_SYSTEM_ALLOWED_URL;
      } else {
        url = AFFILIATE_PAYMENT_SYSTEM_ALLOWED_URL;
      }

      return fetchGet<IPaymentSystemsAllowedResponse>(url, {
        signal,
        urlParams: {
          id: affId.value,
        },
      });
    },
    select: (data) => {
      const response: IPaymentSystemWithdrawalSchema[] = [];

      for (const system of data.payment_systems) {
        const [key, systemData] = Object.entries(system)[0];
        response.push({
          ...systemData,
          payment_system_name: key,
        });
      }

      return response;
    },
    enabled: computed(() => !isAdminNamespace.value || !!affId.value),
  });
}
